import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ScrollRestorationService {
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.initializeScrollRestoration();
  }

  private initializeScrollRestoration(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Listen to navigation events
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Small delay to ensure the new page is fully rendered
        setTimeout(() => {
          this.scrollToTop();
        }, 100);
      });
  }

  /**
   * Scrolls the page to the top, including custom scroll containers
   */
  public scrollToTop(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Scroll main window to top
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });

    // Also scroll any custom scroll containers to top
    this.scrollCustomContainersToTop();
  }

  /**
   * Scrolls custom scroll containers to top
   */
  private scrollCustomContainersToTop(): void {
    // Find all elements with custom scroll containers
    const scrollContainers = document.querySelectorAll(
      '[style*="overflow-y: scroll"], .order-checkout__container__review-order-details, .order-checkout__container__review-order-summary'
    );

    scrollContainers.forEach((container: Element) => {
      const htmlElement = container as HTMLElement;
      if (htmlElement.scrollTo) {
        htmlElement.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      } else {
        // Fallback for older browsers
        htmlElement.scrollTop = 0;
        htmlElement.scrollLeft = 0;
      }
    });
  }

  /**
   * Scrolls the page to the top immediately (without smooth behavior)
   */
  public scrollToTopImmediate(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Scroll main window to top immediately
    window.scrollTo(0, 0);

    // Also scroll custom containers immediately
    const scrollContainers = document.querySelectorAll(
      '[style*="overflow-y: scroll"], .order-checkout__container__review-order-details, .order-checkout__container__review-order-summary'
    );

    scrollContainers.forEach((container: Element) => {
      const htmlElement = container as HTMLElement;
      htmlElement.scrollTop = 0;
      htmlElement.scrollLeft = 0;
    });
  }

  /**
   * Clean up subscriptions
   */
  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
